import { blue, green, grey, indigo, red, teal } from "@mui/material/colors";

export interface Variant {
  name: string;
  palette: {
    primary: { main: string; contrastText: string };
    secondary: { main: string; contrastText: string };
    teal?: { main: string; contrastText: string };
  };
  header: {
    color: string;
    background: string;
    search: { color: string };
    indicator: { background: string };
  };
  sidebar: {
    color: string;
    background: string;
    header: {
      color: string;
      background: string;
      brand: { color: string };
    };
    footer: {
      color: string;
      background: string;
      online: { background: string };
    };
    category: { fontWeight: number };
    badge: { color: string; background: string };
  };
  body: { background: string };
  section?: { background: string };
}

const blueVariant: Variant = {
  name: "Blue",
  palette: {
    primary: {
      main: blue[800],
      contrastText: "#FFF",
    },
    secondary: {
      main: blue[600],
      contrastText: "#FFF",
    },
  },
  header: {
    color: grey[500],
    background: "#FFF",
    search: {
      color: grey[800],
    },
    indicator: {
      background: blue[600],
    },
  },
  sidebar: {
    color: "#FFF",
    background: blue[700],
    header: {
      color: "#FFF",
      background: blue[800],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: "#FFF",
      background: blue[800],
      online: {
        background: "#FFF",
      },
    },
    category: {
      fontWeight: 400,
    },
    badge: {
      color: "#000",
      background: "#FFF",
    },
  },
  body: {
    background: "#F7F9FC",
  },
};

const greenVariant: Variant = {
  name: "Green",
  palette: {
    primary: {
      main: green[800],
      contrastText: "#FFF",
    },
    secondary: {
      main: green[500],
      contrastText: "#FFF",
    },
  },
  header: {
    color: grey[500],
    background: "#FFF",
    search: {
      color: grey[800],
    },
    indicator: {
      background: green[500],
    },
  },
  sidebar: {
    color: "#FFF",
    background: green[700],
    header: {
      color: "#FFF",
      background: green[800],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: "#FFF",
      background: green[800],
      online: {
        background: "#FFF",
      },
    },
    category: {
      fontWeight: 400,
    },
    badge: {
      color: "#000",
      background: "#FFF",
    },
  },
  body: {
    background: "#F9F9FC",
  },
};

const indigoVariant: Variant = {
  name: "Indigo",
  palette: {
    primary: {
      main: indigo[600],
      contrastText: "#FFF",
    },
    secondary: {
      main: indigo[400],
      contrastText: "#FFF",
    },
  },
  header: {
    color: grey[500],
    background: "#FFF",
    search: {
      color: grey[800],
    },
    indicator: {
      background: indigo[500],
    },
  },
  sidebar: {
    color: "#FFF",
    background: indigo[600],
    header: {
      color: "#FFF",
      background: indigo[700],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: "#FFF",
      background: indigo[700],
      online: {
        background: "#FFF",
      },
    },
    category: {
      fontWeight: 400,
    },
    badge: {
      color: "#000",
      background: "#FFF",
    },
  },
  body: {
    background: "#F9F9FC",
  },
};

const tealVariant: Variant = {
  name: "Teal",
  palette: {
    primary: {
      main: teal[800],
      contrastText: "#FFF",
    },
    secondary: {
      main: teal[600],
      contrastText: "#FFF",
    },
  },
  header: {
    color: grey[500],
    background: "#FFF",
    search: {
      color: grey[800],
    },
    indicator: {
      background: teal[600],
    },
  },
  sidebar: {
    color: "#FFF",
    background: teal[700],
    header: {
      color: "#FFF",
      background: teal[800],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: "#FFF",
      background: teal[800],
      online: {
        background: "#FFF",
      },
    },
    category: {
      fontWeight: 400,
    },
    badge: {
      color: "#000",
      background: "#FFF",
    },
  },
  body: {
    background: "#F7F9FC",
  },
};

const lightVariant: Variant = {
  name: "Light",
  palette: {
    primary: {
      main: blue[800],
      contrastText: "#FFF",
    },
    secondary: {
      main: blue[600],
      contrastText: "#FFF",
    },
  },
  header: {
    color: grey[200],
    background: blue[800],
    search: {
      color: grey[100],
    },
    indicator: {
      background: red[700],
    },
  },
  sidebar: {
    color: grey[900],
    background: "#FFF",
    header: {
      color: "#FFF",
      background: blue[800],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: grey[900],
      background: grey[100],
      online: {
        background: green[500],
      },
    },
    category: {
      fontWeight: 600,
    },
    badge: {
      color: "#FFF",
      background: green[600],
    },
  },
  body: {
    background: "#F7F9FC",
  },
};

const darkVariant: Variant = {
  name: "Dark",
  palette: {
    primary: {
      main: "#1b5691",
      contrastText: "#FFF",
    },
    secondary: {
      main: "#4cacde",
      contrastText: "#FFF",
    },
    teal: {
      main: "#216869",
      contrastText: "#FFF",
    },
  },
  header: {
    color: "#FFF",
    background: "#1b5691",
    search: {
      color: "#FFF",
    },
    indicator: {
      background: blue[600],
    },
  },
  sidebar: {
    color: grey[200],
    background: "#254977",
    header: {
      color: grey[200],
      background: "#1b5691",
      brand: {
        color: blue[500],
      },
    },
    footer: {
      color: grey[200],
      background: "#1b5691",
      online: {
        background: green[500],
      },
    },
    category: {
      fontWeight: 400,
    },
    badge: {
      color: "#FFF",
      background: blue[500],
    },
  },
  body: {
    background: "#F7F9FC",
  },
  section: {
    background: green[500],
  },
};

const variants: Variant[] = [
  darkVariant,
  lightVariant,
  blueVariant,
  greenVariant,
  indigoVariant,
  tealVariant,
];

export default variants;
